import * as React from 'react'
import { ArrowLeft, Download, GitCommitHorizontal, Loader2, Pencil, RotateCcw } from 'lucide-react'
import {
  useListCommitsQuery,
  type Branch,
  type Commit
} from '@axilon-apps/domains-ontology-ui-state'
import { formatRelativeTime } from '../../lib/time'
import { Button } from '../ui/button'
import { ScrollArea } from '../ui/scroll-area'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet'
import { BranchRevertCommitDialog } from './BranchRevertCommitDialog'
import { BranchRestoreDialog } from './BranchRestoreDialog'
import { CommitMessageEditDialog } from './CommitMessageEditDialog'
import { CommitDetail } from './CommitDetail'

export interface BranchHistoryDrawerProps {
  branch: Branch
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * The branch's first-parent commit history, newest first, with cursor-token
 * "load more" paging; clicking a row swaps the panel to that commit's detail
 * (what the commit changed, rendered from its stored delta). Rows on the
 * branch's current segment offer restore/revert actions; inherited rows keep
 * those graph-changing actions hidden. For an unprotected branch, restore
 * rebuilds the working copy in place; for a protected branch the server checks
 * the commit out into a new branch instead.
 */
export function BranchHistoryDrawer({ branch, open, onOpenChange }: BranchHistoryDrawerProps) {
  const [pageToken, setPageToken] = React.useState<string | undefined>(undefined)
  const [selected, setSelected] = React.useState<string | null>(null)
  const [restoreTarget, setRestoreTarget] = React.useState<string | null>(null)
  const [revertTarget, setRevertTarget] = React.useState<string | null>(null)
  const [editTarget, setEditTarget] = React.useState<Commit | null>(null)
  // Async commits land with their delta still computing; the worker's merge
  // wake-up refetches this entry so a "computing changes…" chip resolves in place.
  const { data, isLoading, isFetching, error } = useListCommitsQuery(
    { branchId: branch.id, ...(pageToken ? { pageToken } : {}) },
    { skip: !open }
  )
  const commits = data?.commits ?? []

  React.useEffect(() => {
    if (!open) {
      setPageToken(undefined)
      setSelected(null)
      setRestoreTarget(null)
      setRevertTarget(null)
      setEditTarget(null)
    }
  }, [open])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-2xl">
        <SheetHeader>
          {selected ? (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Back to history"
                onClick={() => setSelected(null)}
              >
                <ArrowLeft />
              </Button>
              <SheetTitle>Commit {selected.slice(0, 8)}</SheetTitle>
            </div>
          ) : (
            <SheetTitle>History — {branch.name}</SheetTitle>
          )}
          <SheetDescription>
            {selected
              ? 'What this commit changed.'
              : 'Current and inherited commits, newest first.'}
          </SheetDescription>
        </SheetHeader>

        {/* Radix's ScrollArea wraps children in a `display:table` div that
            grows to its widest child; with a long unbreakable commit message
            that defeats each row's `truncate` and pushes the shrink-0 Restore
            button off the panel's right edge (rendered, but out of view).
            Force that wrapper back to `block` so rows constrain to the panel
            width and the Restore button stays visible. */}
        <ScrollArea className="min-h-0 flex-1 px-4 pb-4 [&_[data-radix-scroll-area-viewport]>div]:block!">
          {selected ? (
            <CommitDetail hash={selected} />
          ) : (
            <div className="flex flex-col">
              {error ? (
                <p role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
                  Failed to load history.
                </p>
              ) : null}

              {commits.map((commit, index) => (
                <React.Fragment key={commit.hash}>
                  {commit.inherited && !commits[index - 1]?.inherited ? (
                    <div className="border-b border-line bg-st-idle-bg/40 px-3 py-1.5 text-11 font-medium uppercase tracking-wide text-ink-3">
                      Inherited history
                    </div>
                  ) : null}
                  <HistoryRow
                    commit={commit}
                    isForkPoint={branch.forkedFrom === commit.hash}
                    onSelect={setSelected}
                    onEdit={setEditTarget}
                    onRestore={setRestoreTarget}
                    onRevert={setRevertTarget}
                  />
                </React.Fragment>
              ))}

              {isLoading || isFetching ? (
                <p className="flex items-center gap-2 py-2.5 text-xs text-ink-3">
                  <Loader2 className="size-3 animate-spin" />
                  Loading commits…
                </p>
              ) : null}

              {!isLoading && (data?.commits.length ?? 0) === 0 && !error ? (
                <p className="py-2.5 text-xs text-ink-3">No commits yet.</p>
              ) : null}

              {data?.nextPageToken ? (
                <Button
                  variant="ghost"
                  size="xs"
                  className="mt-1 self-start"
                  disabled={isFetching}
                  onClick={() => setPageToken(data.nextPageToken)}
                >
                  Load more
                </Button>
              ) : null}
            </div>
          )}
        </ScrollArea>

        <BranchRestoreDialog
          branch={branch}
          commit={restoreTarget ?? undefined}
          open={restoreTarget !== null}
          onOpenChange={(next) => {
            if (!next) setRestoreTarget(null)
          }}
        />
        <BranchRevertCommitDialog
          branch={branch}
          commit={revertTarget ?? undefined}
          open={revertTarget !== null}
          onOpenChange={(next) => {
            if (!next) setRevertTarget(null)
          }}
        />
        <CommitMessageEditDialog
          commit={editTarget ?? undefined}
          open={editTarget !== null}
          onOpenChange={(next) => {
            if (!next) setEditTarget(null)
          }}
        />
      </SheetContent>
    </Sheet>
  )
}

function HistoryRow({
  commit,
  isForkPoint,
  onSelect,
  onEdit,
  onRestore,
  onRevert
}: {
  commit: Commit
  isForkPoint: boolean
  onSelect: (hash: string) => void
  onEdit: (commit: Commit) => void
  onRestore: (hash: string) => void
  onRevert: (hash: string) => void
}) {
  // The message's first line is the title (git convention); the body shows in
  // the detail view only.
  const titleLine = commit.message?.split('\n', 1)[0]
  const showRevert = !commit.inherited && !commit.deltaPending && !commit.deltaEmpty
  return (
    <div
      className={`flex w-full items-center gap-2.5 border-b border-line px-3 py-2.5 last:border-b-0 hover:bg-st-idle-bg/40 ${
        commit.inherited ? 'bg-st-idle-bg/20' : ''
      }`}
    >
      <button
        type="button"
        onClick={() => onSelect(commit.hash)}
        className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5 text-left"
      >
        <GitCommitHorizontal className="mt-0.5 size-3.5 shrink-0 text-ink-3" />
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm text-ink">{titleLine || commit.hash.slice(0, 8)}</span>
          <span className="text-xs text-ink-3">
            {[commit.hash.slice(0, 8), commit.author, formatRelativeTime(commit.createdAt)]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {commit.inherited ? (
            <span className="w-fit rounded-full bg-st-idle-bg px-1.5 py-0.5 text-10 font-medium text-ink-3">
              Inherited
            </span>
          ) : isForkPoint ? (
            <span className="w-fit rounded-full bg-st-info-bg px-1.5 py-0.5 text-10 font-medium text-st-info">
              Fork point
            </span>
          ) : null}
          {commit.deltaPending ? (
            <span className="flex items-center gap-1 text-11 text-ink-3">
              <Loader2 className="size-2.5 animate-spin" />
              Computing changes…
            </span>
          ) : null}
        </span>
      </button>
      <Button
        variant="outline"
        size="xs"
        className="shrink-0"
        aria-label={`Edit message for commit ${commit.hash.slice(0, 8)}`}
        title="Edit commit message"
        onClick={() => onEdit(commit)}
      >
        <Pencil />
        Edit message
      </Button>
      {!commit.inherited ? (
        <Button
          variant="outline"
          size="xs"
          className="shrink-0 border-st-info/30 bg-st-info-bg text-st-info hover:bg-st-info-bg/70 hover:text-st-info"
          aria-label={`Restore to commit ${commit.hash.slice(0, 8)}`}
          title="Restore the branch working copy to this point"
          onClick={() => onRestore(commit.hash)}
        >
          <Download />
          Restore
        </Button>
      ) : null}
      {showRevert ? (
        <Button
          variant="outline"
          size="xs"
          className="shrink-0 border-st-warn/40 bg-st-warn-bg text-st-warn hover:bg-st-warn-bg/70 hover:text-st-warn"
          aria-label={`Revert commit ${commit.hash.slice(0, 8)}`}
          title="Create a new commit that undoes this commit"
          onClick={() => onRevert(commit.hash)}
        >
          <RotateCcw />
          Revert
        </Button>
      ) : null}
    </div>
  )
}
